import React from 'react'
import PropTypes from 'prop-types';

import BaseComponent from 'common/BaseComponent'
import {getMessage as msg} from 'MessageBundle'

import sortAsc from './arrowUp.png';
import sortDesc from './arrowDown.png';
import sortOther from './arrows.png';
import theme from 'theme/theme.scss'

export const SORT_ORDER = {
    ASC: "ascending",
    DESC: "descending",
    OTHER: "other"
};

const DATE_ATTRIBUTES = ["created"];

/**
 * Default implementation of the table row.
 */
class TableRow extends React.Component {

    static propTypes = {
        /**
         * JavaScript function that has to be executed on table row click.
         */
        onClick: PropTypes.func,

        /**
         * True if the table row should be selectable.
         */
        selectable: PropTypes.bool
    };

    render() {
        return (
            <tr className={this.props.selectable ? theme.selectable : null} onClick={this.props.onClick}>
                {this.props.children}
            </tr>
        );
    }
}

/**
 * Implementation of the table header.
 */
class TableHeader extends React.Component {

    static propTypes = {
        /**
         * JavaScript function that has to be executed on table header click.
         */
        onClick: PropTypes.func,

        /**
         * Current table sort order. It can be equal one of the following value {@link #SORT_ORDER}.
         */
        sortOrder: PropTypes.string,

        /**
         * CSS class name.
         */
        className: PropTypes.string,

        /**
         * Link title attribute to be shown on mouseover.
         */
        title: PropTypes.string,

        /**
         * Boolean flag indicating that table has fixed header.
         */
        fixedHeader: PropTypes.bool
    };

    constructor(props) {
        super(props);

        this.sortIcon = new Map([
            [SORT_ORDER.ASC, {icon: sortAsc, title: msg("table.ascending")}],
            [SORT_ORDER.DESC, {icon: sortDesc, title: msg("table.descending")}],
            [SORT_ORDER.OTHER, {icon: sortOther, title: msg("table.notSorted")}]
        ]);
    }

    render() {
        const {children, onClick, className, sortOrder, width, title} = this.props;
        return (
            <th className={className} style={width ? {width: width} : {}}>
                <div className={this.props.fixedHeader ? theme.thInner : ""}>
                {onClick ? (
                    <a href="javascript:void(0)" aria-sort={sortOrder !== SORT_ORDER.OTHER ? sortOrder : undefined} onClick={onClick} title={title}>
                        <img src={this.sortIcon.get(sortOrder).icon} alt=""/>
                        {children}
                    </a>
                    ) : <span title={title}>{children}</span>}
                </div>
            </th>
        );
    }
}

TableHeader.defaultProps = {
    fixedHeader: true
};

/**
 * Renders the html table. Implemented to satisfy all ARIA and Deutsche Telekom web accessibility requirements.
 */
export default class Table extends BaseComponent {

    constructor(props) {
        super(props);

        this.nextGeneratedRowId = 1;

        this.state = {
            sortField: props.defaultSortColumn,
            sortOrder: props.defaultSortOrder
        };
    }

    componentDidMount() {
        if (this.props.lastRowStructure !== undefined && this.props.structure.length !== this.props.lastRowStructure.length) {
            throw new Error("Table Structure Length != Last Row Structure Length");
        }
        if (this.props.firstRowStructure !== undefined && this.props.structure.length !== this.props.firstRowStructure.length) {
            throw new Error("Table Structure Length != First Row Structure Length");
        }
    }

    retrieveOrder = (columnId) => {
        return columnId === this.state.sortField ? this.state.sortOrder : SORT_ORDER.OTHER;
    };

    nextOrder = (current) => {
        if (current === SORT_ORDER.DESC) {
            return SORT_ORDER.ASC;
        } else if (current === SORT_ORDER.ASC) {
            return SORT_ORDER.DESC;
        } else {
            return this.props.defaultSortOrder || SORT_ORDER.DESC;
        }
    };

    sortedRows = () => {
        let descriptor = this.props.structure.find(d => d.attribute === this.state.sortField);
        let values = this.props.value.slice();

        let orderFactor = this.state.sortOrder === SORT_ORDER.ASC ? 1 : -1;
        return values.sort((a, b) => {
            let first;
            let second;
            if (typeof a[descriptor.attribute] === "number" || typeof b[descriptor.attribute] === "number") {
                first = a[descriptor.attribute] || "";
                second = b[descriptor.attribute] || "";
            } else if (DATE_ATTRIBUTES.includes(descriptor.attribute)) {
                let fixedDateStrings = this.fixDateStringsForBrowser([ a[descriptor.attribute], b[descriptor.attribute] ]);
                first = new Date(fixedDateStrings[0]);
                second = new Date(fixedDateStrings[1]);
            } else {
                first = this.extractValueToSort(descriptor.render(a));
                second = this.extractValueToSort(descriptor.render(b));
            }
            return first > second ? orderFactor : first < second ? -orderFactor : 0;
        });
    };

    /**
     * This method changes datetime strings like "2020-09-10T08:30:39.654+0000" to "2020-09-10T08:30:39.654+00:00"
     * in order to make them compatible the datetime parsing implementation in browser IE11.
     * Otherwise we have "Invalid date" errors.
     * This is important for sorting the table entries by the datetime column values (the column "created").
     * @param dateStrings an array of datetime strings to fix.
     * @return a new array of the fixed datetime strings which can be passed to the Date "class" constructor.
     */
    fixDateStringsForBrowser = (dateStrings) => {
        if (this.browserIs("IE11")) {
            let fixedDateStrings = Array(dateStrings.length);
            const wrongIE11DateEnding = /(.*[+\-])(\d{4})/;
            for (let i = 0; i < dateStrings.length; i++) {
                let dateString = dateStrings[i];
                let erroneousDateMatching = dateString.match(wrongIE11DateEnding);
                if (erroneousDateMatching != null) {
                    let erroneousLocalDatetime = erroneousDateMatching[1];
                    let erroneousTimezone = erroneousDateMatching[2];
                    fixedDateStrings[i] = erroneousLocalDatetime + erroneousTimezone.slice(0, 2) + ":" + erroneousTimezone.slice(2, 4);
                } else {
                    fixedDateStrings[i] = dateString;
                }
            }
            return fixedDateStrings;
        } else {
            return dateStrings;
        }
    };

    extractValueToSort = (row) => {
        if(row && row.props && row.props.children){
           return this.extractValueToSort(row.props.children);
        } else {
            return row || "";
        }
    };

    renderHeaders = () => {
        return this.props.structure.map((descriptor, id) => {
            let header;
            if (this.props.sortable && descriptor.sortable !== false) {
                const order = this.retrieveOrder(descriptor.attribute);
                const nextOrder = this.nextOrder(order);
                header = (
                    <TableHeader key={id} onClick={() => {this.setState({sortField: descriptor.attribute, sortOrder: nextOrder})}}
                        sortable sortOrder={order} title={descriptor.title || descriptor.label} fixedHeader={this.props.fixedHeader}
                        className={descriptor.headStyle} width={descriptor.width}>
                        {this.renderHeaderLabel(descriptor)}
                    </TableHeader>
                )
            } else {
                header = (
                    <TableHeader key={id} className={descriptor.headStyle} width={descriptor.width}
                        fixedHeader={this.props.fixedHeader}>
                        {this.renderHeaderLabel(descriptor)}
                    </TableHeader>
                )
            }
            return header;
        });
    };

    renderHeaderLabel = (descriptor) => {
        if (descriptor.fullLabel) {
            return <abbr title={descriptor.fullLabel}>{descriptor.label}</abbr>;
        } else {
            return descriptor.label;
        }
    };

    renderRows = () => {
        const Row = this.props.customRow || TableRow;
        const values = this.props.sortable ? this.sortedRows() : this.props.value;

        const mappedRows = values.map((value, idx) => (
            <Row key={this.generateRowId(value)} value={value} selectable={this.props.selectable} onClick={this.props.onSelect !== undefined ? this.props.onSelect(value) : null}>
                {this.props.structure.map((descriptor, id) => (
                    <td key={id} className={typeof descriptor.bodyStyle === 'function' ? descriptor.bodyStyle(value) : descriptor.bodyStyle} style={descriptor.width ? {width: descriptor.width} : {}  }>
                        {id === 0 && this.props.selectable ? <a href="javascript:void(0)">{descriptor.render(value, idx, this.props.errorMessages)}</a>
                            : descriptor.render(value, idx, this.props.errorMessages)}
                    </td>
                ))}
            </Row>
        ));

        if (this.props.firstRowStructure !== undefined) {
            mappedRows.unshift(
                <Row key="firstRow">
                    {this.props.firstRowStructure.map((descriptor, id) => (
                        <td key={id} className={descriptor.bodyStyle} style={descriptor.width ? {width: descriptor.width} : {}}>
                            {descriptor.render()}
                        </td>
                    ))}
                </Row>
            );
        }

        if (this.props.lastRowStructure !== undefined) {
            mappedRows.push(
                <Row key="lastRow">
                    {this.props.lastRowStructure.map((descriptor, id) => (
                        <td key={id} className={descriptor.bodyStyle} style={descriptor.width ? {width: descriptor.width} : {}}>
                            {descriptor.render()}
                        </td>
                    ))}
                </Row>
            );
        }
        return mappedRows;
    };

    renderCaption = () => {
        if(this.props.caption) {
            return <caption>{this.props.caption}</caption>;
        }
    };

    generateRowId(value) {
        if(!value.generatedRowId) {
            value.generatedRowId = this.nextGeneratedRowId++;
        }
        return value.generatedRowId;
    }

    render()  {
        return (
            <table className={this.props.className}>
                {this.renderCaption()}
                <thead>
                    <tr>
                        {this.renderHeaders()}
                    </tr>
                </thead>
                <tbody>
                    {this.renderRows()}
                </tbody>
            </table>
        );
    }
}

Table.propTypes = {

    /**
     * An array which contains the table structure.
     *
     * Every element could contain the following fields:
     * attribute - a name of the data in the column.
     * label     - a text to show in the column header,
     * fullLabel - an additional label if the shown one is abbreviation
     * title     - a text to show as the column title,
     * render    - function to render the column cells. Gets row object as a parameter,
     * headStyle - css style of the column header (optional),
     * bodyStyle - css style of the column cells (optional).
     *
     * E.g. [{attribute: "column1", label:"label1", "headStyle":"headStyle1", bodyStyle:"bodyStyle1", render: (row) => {return row.field1}},
     *       {attribute: "column2", label:"label2", render: (row) => {return row.field2}}]
     */
    structure: PropTypes.array.isRequired,

    /**
     * An array which contains structure of the first row.
     * It's an optional prop and must has the same interface like the structure prop.
     */
    firstRowStructure: PropTypes.array,

    /**
     * An array which contains structure of the last row.
     * It's an optional prop and must has the same interface like the structure prop.
     */
    lastRowStructure: PropTypes.array,

    /**
     * An array of objects to show in the table. Every object is rendered in separate row.
     */
    value: PropTypes.array.isRequired,

    /**
     * id of column to be sorted by default. Should be one of ids from structure.
     */
    defaultSortColumn: PropTypes.string,

    /**
     * Default sort order (applied for the defaultSortColumn).
     */
    defaultSortOrder: PropTypes.oneOf(Object.values(SORT_ORDER)),

    /**
     * React Component to be rendered instead of default TableRow.
     */
    customRow: PropTypes.func,

    /**
     * True if the table row should be selectable.
     */
    selectable: PropTypes.bool,

    /**
     * JavaScript function that has to be executed on table row click. To apply the function {@code selectable} props must be defined as true.
     */
    onSelect: PropTypes.func,

    /**
     * True if the table columns should be sortable. This behavior can be overridden for specific header if a structure
     * element has a sortable parameter.
     */
    sortable: PropTypes.bool,

    /**
     * CSS class name that has to be applied to table element.
     */
    className: PropTypes.string,

    /**
     * Value of table caption.
     */
    caption: PropTypes.string,

    /**
     * Boolean flag indicating that table has fixed header.
     */
    fixedHeader: PropTypes.bool
};

Table.defaultProps = {
    fixedHeader: true
};



// WEBPACK FOOTER //
// ./src/common/Table/index.js